import { useEffect, useState } from 'react'
import { enregistrerIdentite, type DossierPme, type IdentiteSaisie } from '../../api/onboarding'
import {
  apercuDesignation,
  FORMES_JURIDIQUES,
  LIBELLE_QUALITE,
  LIBELLE_STATUT_JURIDIQUE,
  LIBELLE_TYPE_PIECE,
  QUALITES_REPRESENTANT,
  STATUTS_JURIDIQUES,
  TYPES_PIECE,
  type IdentiteJuridique,
  type StatutJuridique,
} from '../../api/designationPartie'
import { identiteManque, statutDe, type StatutOnboarding } from '../../api/onboardingRegles'
import { ADRESSE_VIDE, aUnPoint, libelleAdresse, type AdresseSaisie } from '../../api/adresses'
import { ListeDeroulante, type OptionListe } from '../ListeDeroulante'
import { SelecteurAdresse } from '../SelecteurAdresse'
import { Bloc, Erreur, Info, useAction } from './Briques'

type Formulaire = Omit<IdentiteSaisie, 'default_pickup_address' | 'default_pickup_lat' | 'default_pickup_lng'>

const LIBELLE_FORME: Record<string, string> = {
  SARL: 'SARL (société à responsabilité limitée)',
  SA: 'SA (société anonyme)',
  SAS: 'SAS (société par actions simplifiée)',
  SNC: 'SNC (société en nom collectif)',
  GIE: 'GIE (groupement d’intérêt économique)',
  SCS: 'SCS (société en commandite simple)',
  autre: 'Autre forme (à préciser)',
}
const OPTIONS_FORME: OptionListe[] = [{ valeur: '', libelle: 'Choisir…' }, ...FORMES_JURIDIQUES.map((f) => ({ valeur: f, libelle: LIBELLE_FORME[f] ?? f }))]
const OPTIONS_QUALITE: OptionListe[] = [
  { valeur: '', libelle: 'Choisir…' },
  ...QUALITES_REPRESENTANT.map((q) => ({ valeur: q, libelle: q === 'autre' ? 'Autre (à préciser)' : (LIBELLE_QUALITE[q] ?? q).replace(/^./, (c) => c.toUpperCase()) })),
]
const OPTIONS_PIECE: OptionListe[] = [{ valeur: '', libelle: 'Choisir…' }, ...TYPES_PIECE.map((t) => ({ valeur: t, libelle: LIBELLE_TYPE_PIECE[t] ?? t }))]

const AIDE_STATUT: Record<StatutJuridique, string> = {
  societe: 'Société immatriculée au RCCM : raison sociale, forme, RCCM, NIF, siège et représentant légal.',
  entreprenant: 'Entreprenant déclaré (Acte uniforme OHADA) : pas de RCCM exigé ; numéro de déclaration, nom et adresse. NIF facultatif.',
  personne_physique: 'Simple particulier professionnel, ni société ni entreprenant : état civil, pièce d’identité et adresse.',
}

/** La fiche, vue comme une identité juridique (capital nettoyé de ses espaces : « 1 000 000 » → 1000000). */
function versIdentite(f: Formulaire): IdentiteJuridique {
  return { ...f, capital_fcfa: f.capital_fcfa.replace(/\D/g, '') }
}

/**
 * Identité du client. LE STATUT JURIDIQUE D'ABORD (société, entreprenant, personne physique) : il décide des champs
 * affichés, des champs exigés avant l'envoi du contrat et des pièces à fournir, et de la phrase qui désigne le
 * client dans le contrat — dont l'aperçu s'écrit en direct (formulation à valider par le juriste).
 * Une fois le contrat envoyé, la base FIGE toute l'identité (le texte envoyé doit rester vrai) : ces champs se
 * grisent ; contact, téléphone, e-mail et collecte restent modifiables. Retirer le contrat pour corriger.
 */
export function SectionIdentite({
  dossier,
  statut,
  modifiable,
  surChange,
  surStatutJuridique,
}: {
  dossier: DossierPme
  statut: StatutOnboarding
  modifiable: boolean
  surChange: () => void
  /** Prévient le dossier dès qu'un statut juridique est CHOISI, pour que la liste des pièces suive sans attendre l'enregistrement. */
  surStatutJuridique?: (s: StatutJuridique | null) => void
}) {
  const fige = statut === 'contrat_envoye' || statut === 'contrat_signe'
  const s = (v: string | null | undefined) => v ?? ''
  const [f, setF] = useState<Formulaire>({
    name: dossier.name,
    contact_name: s(dossier.contact_name),
    phone: s(dossier.phone),
    email: s(dossier.email),
    statut_juridique: statutDe(dossier) ?? '',
    forme_juridique: s(dossier.forme_juridique),
    forme_juridique_precision: s(dossier.forme_juridique_precision),
    raison_sociale: s(dossier.raison_sociale),
    nom_commercial: s(dossier.nom_commercial),
    capital_fcfa: dossier.capital_fcfa == null ? '' : String(dossier.capital_fcfa),
    rccm: s(dossier.rccm),
    nif: s(dossier.nif),
    numero_declaration_entreprenant: s(dossier.numero_declaration_entreprenant),
    siege_adresse: s(dossier.siege_adresse),
    representant_nom: s(dossier.representant_nom ?? dossier.gerant_nom),
    representant_qualite: s(dossier.representant_qualite),
    representant_qualite_precision: s(dossier.representant_qualite_precision),
    personne_nom: s(dossier.personne_nom),
    personne_prenoms: s(dossier.personne_prenoms),
    date_naissance: s(dossier.date_naissance),
    lieu_naissance: s(dossier.lieu_naissance),
    type_piece: s(dossier.type_piece),
    type_piece_precision: s(dossier.type_piece_precision),
    numero_piece: s(dossier.numero_piece),
    adresse_personne: s(dossier.adresse_personne),
  })
  const [collecte, setCollecte] = useState<AdresseSaisie>({
    ...ADRESSE_VIDE,
    texte: dossier.default_pickup_address ?? '',
    lat: dossier.default_pickup_lat,
    lng: dossier.default_pickup_lng,
  })
  const { occupe, erreur, info, lancer } = useAction()
  const maj = (patch: Partial<Formulaire>) => setF((x) => ({ ...x, ...patch }))

  const identite = versIdentite(f)
  const choix = statutDe(identite)
  const manque = identiteManque(identite)
  const apercu = apercuDesignation(identite)
  const enregistre = statutDe(dossier)

  function enregistrer() {
    void lancer(async () => {
      await enregistrerIdentite(dossier.id, {
        ...f,
        default_pickup_address: libelleAdresse(collecte),
        default_pickup_lat: aUnPoint(collecte) ? collecte.lat : null,
        default_pickup_lng: aUnPoint(collecte) ? collecte.lng : null,
      })
      surChange()
      return 'Identité enregistrée.'
    })
  }

  const ro = !modifiable
  const bloque = ro || fige
  const champ = (cle: keyof Formulaire, libelle: string, extra?: { placeholder?: string; plein?: boolean; type?: string }) => (
    <div className={`field${extra?.plein ? ' plein' : ''}`}>
      <label>{libelle}</label>
      <input
        type={extra?.type ?? 'text'}
        value={f[cle]}
        disabled={bloque}
        placeholder={extra?.placeholder}
        onChange={(e) => maj({ [cle]: e.target.value } as Partial<Formulaire>)}
      />
    </div>
  )

  useEffect(() => {
    surStatutJuridique?.((f.statut_juridique || null) as StatutJuridique | null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.statut_juridique])

  return (
    <Bloc titre="Identité du client" lecture={ro}>
      <div className="field">
        <label>Statut juridique *</label>
        <div className="onb-chips" role="radiogroup" aria-label="Statut juridique">
          {STATUTS_JURIDIQUES.map((j) => (
            <button
              key={j}
              type="button"
              role="radio"
              aria-checked={f.statut_juridique === j}
              className={`onb-chip${f.statut_juridique === j ? ' on' : ''}`}
              disabled={bloque}
              onClick={() => maj({ statut_juridique: j })}
            >
              {LIBELLE_STATUT_JURIDIQUE[j]}
            </button>
          ))}
        </div>
        {choix ? <p className="onb-note">{AIDE_STATUT[choix]}</p> : <p className="onb-note">Choisissez d’abord le statut : il détermine les champs, les pièces et la façon dont le client est désigné dans le contrat.</p>}
        {(f.statut_juridique || '') !== (enregistre ?? '') && <p className="onb-note">Enregistrez l’identité pour que la liste des pièces suive ce statut.</p>}
      </div>

      <div className="onb-champs">
        {champ('name', 'Nom du client (affiché dans la console) *', { plein: true })}

        {choix === 'societe' && (
          <>
            {champ('raison_sociale', 'Raison sociale (dénomination légale) *', { plein: true, placeholder: 'ex. Boutique Lafia SARL' })}
            <div className="field">
              <label>Forme juridique *</label>
              <ListeDeroulante valeur={f.forme_juridique} options={OPTIONS_FORME} desactive={bloque} surChangement={(v) => maj({ forme_juridique: v })} pleineLargeur />
            </div>
            {f.forme_juridique === 'autre' ? champ('forme_juridique_precision', 'Précisez la forme juridique *', { placeholder: 'ex. SUARL' }) : champ('capital_fcfa', 'Capital social (FCFA, facultatif)', { placeholder: 'ex. 1 000 000' })}
            {f.forme_juridique === 'autre' && champ('capital_fcfa', 'Capital social (FCFA, facultatif)', { placeholder: 'ex. 1 000 000' })}
            {champ('rccm', 'RCCM *', { placeholder: 'ex. ML-BKO-2024-B-1234' })}
            {champ('nif', 'NIF *')}
            {champ('siege_adresse', 'Adresse du siège *', { plein: true, placeholder: 'ex. à Lafiabougou, rue 123, Bamako' })}
            {champ('representant_nom', 'Représentant légal (nom complet) *')}
            <div className="field">
              <label>Qualité du représentant *</label>
              <ListeDeroulante valeur={f.representant_qualite} options={OPTIONS_QUALITE} desactive={bloque} surChangement={(v) => maj({ representant_qualite: v })} pleineLargeur />
            </div>
            {f.representant_qualite === 'autre' && champ('representant_qualite_precision', 'Précisez la qualité *', { placeholder: 'ex. secrétaire général' })}
          </>
        )}

        {choix === 'entreprenant' && (
          <>
            {champ('personne_nom', 'Nom de l’entreprenant *')}
            {champ('personne_prenoms', 'Prénoms *')}
            {champ('nom_commercial', 'Nom commercial (facultatif si nom et prénoms sont renseignés)', { plein: true, placeholder: 'ex. Chez Sira' })}
            {champ('numero_declaration_entreprenant', 'Numéro de déclaration d’entreprenant *', { plein: true })}
            {champ('adresse_personne', 'Adresse (domicile) *', { plein: true, placeholder: 'ex. à Badalabougou, Bamako' })}
            {champ('siege_adresse', 'Adresse de l’établissement (si différente)', { plein: true })}
            {champ('nif', 'NIF (facultatif)')}
          </>
        )}

        {choix === 'personne_physique' && (
          <>
            {champ('personne_nom', 'Nom *')}
            {champ('personne_prenoms', 'Prénoms *')}
            {champ('date_naissance', 'Date de naissance (facultatif)', { type: 'date' })}
            {champ('lieu_naissance', 'Lieu de naissance (facultatif)', { placeholder: 'ex. Kayes' })}
            <div className="field">
              <label>Pièce d’identité *</label>
              <ListeDeroulante valeur={f.type_piece} options={OPTIONS_PIECE} desactive={bloque} surChangement={(v) => maj({ type_piece: v })} pleineLargeur />
            </div>
            {f.type_piece === 'autre' ? champ('type_piece_precision', 'Précisez la pièce *', { placeholder: 'ex. permis de conduire' }) : champ('numero_piece', 'Numéro de la pièce *')}
            {f.type_piece === 'autre' && champ('numero_piece', 'Numéro de la pièce *')}
            {champ('adresse_personne', 'Adresse (domicile) *', { plein: true, placeholder: 'ex. à Sogoniko, Bamako' })}
          </>
        )}

        <div className="field">
          <label>Interlocuteur</label>
          <input value={f.contact_name} disabled={ro} onChange={(e) => maj({ contact_name: e.target.value })} />
        </div>
        <div className="field">
          <label>Téléphone (WhatsApp)</label>
          <input value={f.phone} disabled={ro} placeholder="+223 …" onChange={(e) => maj({ phone: e.target.value })} />
        </div>
        <div className="field plein">
          <label>E-mail du signataire (exigé par Documenso)</label>
          <input type="email" value={f.email} disabled={ro} onChange={(e) => maj({ email: e.target.value })} />
        </div>
      </div>

      {apercu && (
        <div className="onb-apercu-contrat">
          <div className="titre">Aperçu dans le contrat <span>(formulation à valider par le juriste)</span></div>
          <p>{apercu} <em>ci-après le « Client »,</em></p>
        </div>
      )}
      {fige && <p className="onb-note">Le contrat est parti : l’identité est figée. Retirez le contrat pour la corriger.</p>}
      {manque.length > 0 && !fige && (
        <div className="onb-alerte">
          Avant l’envoi du contrat, il manque : <b>{manque.join(', ')}</b>.
        </div>
      )}
      {manque.length === 0 && !fige && choix && <div className="onb-ok">Identité complète pour ce statut juridique.</div>}

      <div style={{ marginTop: 6 }}>
        <SelecteurAdresse
          titre="Collecte habituelle (épingle)"
          valeur={collecte}
          surChange={ro ? () => undefined : setCollecte}
          libelleConnues=""
          placeholderTexte="ex. Boutique Lafia, marché de Lafiabougou"
          placeholderRepere="ex. face à la pharmacie, portail bleu"
        />
      </div>

      {!ro && (
        <div className="onb-actions">
          <button className="btn btn-gold btn-sm" disabled={occupe} onClick={enregistrer}>
            {occupe ? 'Enregistrement…' : 'Enregistrer l’identité'}
          </button>
        </div>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </Bloc>
  )
}
